import React, { useState,useRef  } from "react";
import styles from "./SIPCalculator.module.css";
import { FaFilePdf } from "react-icons/fa";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import PdfLayout from "./PDF/Layout";
import ResultCard from "./SIPCalculatorResult";

const SIPCalculator = () => {
  const pdfRef = useRef(null);
  const [form, setForm] = useState({
    presentAge: "",
    targetAge: "",
    monthlyInvestment: "",
    cagr: "",
    stepUpType:"percentage",
    annualStepUp:""
  });
  const [result, setResult] = useState(null);
  const [stepUpResult, setstepUpResult] = useState(null);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));
  };



  const handleDownloadPdf = async () => {
      
        const element = pdfRef.current;

        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
        });

        const imgData = canvas.toDataURL("image/png");

        const pdf = new jsPDF("p", "mm", "a4");

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        // fit image into one page (both width + height constraints)
        let imgWidth = pageWidth;
        let imgHeight = (canvas.height * imgWidth) / canvas.width;

        // if image is too tall, scale it down to fit page height
        if (imgHeight > pageHeight) {
          const scaleFactor = pageHeight / imgHeight;
          imgWidth = imgWidth * scaleFactor;
          imgHeight = pageHeight;
        }

        const x = (pageWidth - imgWidth) / 2; // optional center alignment
        const y = 0;

        pdf.addImage(imgData, "PNG", x, y, imgWidth, imgHeight);

        pdf.save("SIP-CALCULATOR-REPORT.pdf");
  };

  
 const calculateRetirementSIP = ({
  presentAge,
  targetAge,
  monthlyInvestment,
  cagr,
  stepUpValue,
  stepUpType = "percentage" // "percentage" | "absolute"
}) => {
  const years = targetAge - presentAge;
  if (years <= 0) {
    throw new Error("Target age must be greater than present age");
  }

  const safeStepUpValue = Number(stepUpValue) || 0;
  monthlyInvestment = Number(monthlyInvestment);
  cagr = Number(cagr);

  const monthlyRate = cagr / 100 / 12;
  const totalMonths = years * 12;

  // WITHOUT STEP-UP
  const totalInvestmentWithout = monthlyInvestment * totalMonths;

  let maturityWithout;
  if (monthlyRate === 0) {
    maturityWithout = monthlyInvestment * totalMonths;
  } else {
    const factor = Math.pow(1 + monthlyRate, totalMonths) - 1;
    maturityWithout = monthlyInvestment * (factor / monthlyRate);
  }


  // WITH STEP-UP
  let totalInvestmentWith = 0;
  let maturityWith = 0;
  let currentSip = monthlyInvestment;
  for (let year = 0; year < years; year++) {
    for (let month = 1; month <= 12; month++) {
      totalInvestmentWith += currentSip;
      const currentMonth = year * 12 + month;
      const remainingMonths = totalMonths - currentMonth + 1;
      maturityWith += currentSip * Math.pow(1 + monthlyRate, remainingMonths);
    }
    if (stepUpType === "percentage") {
      currentSip *= 1 + safeStepUpValue / 100;
    } else {
      currentSip += safeStepUpValue;
    }
  }

  return {
    withoutStepUp: {
      totalSavingsTillRetirement: (totalInvestmentWithout),
      amountOnMaturity: (maturityWithout),
      amountOnMaturityCrores: +(maturityWithout / 10000000),
    },
    withStepUp: {
      totalSavingsTillRetirement: Math.round(totalInvestmentWith),
      amountOnMaturity: Math.round(maturityWith),
      amountOnMaturityCrores: +(maturityWith / 10000000).toFixed(2),
    },
  };
};

  const calculateSIP = () => {
    setError("");
    setResult(null);
    setstepUpResult(null)

    const presentAge = Number(form.presentAge);
    const targetAge = Number(form.targetAge);
    const monthlyInvestment = Number(form.monthlyInvestment);
    const cagr = Number(form.cagr);
    const annualStepUp= Number(form.annualStepUp) 
    const stepUpType=form.stepUpType
    // validation
    if (
      Number.isNaN(presentAge) ||
      Number.isNaN(targetAge) ||
      Number.isNaN(monthlyInvestment) ||
      Number.isNaN(cagr)
    ) {
      setError("Please enter numeric values for all fields.");
      return;
    }
    if (presentAge < 0 || targetAge <= presentAge) {
      setError("Please ensure Present Age >= 0 and Target Age > Present Age.");
      return;
    }
    if (monthlyInvestment <= 0 || cagr <= 0) {
      setError("Monthly investment and CAGR must be greater than zero.");
      return;
    }

    //call stepupcalculator:
    if(form.annualStepUp){

      const  stepupResult = calculateRetirementSIP({presentAge,targetAge,monthlyInvestment,cagr,stepUpValue:annualStepUp, stepUpType})
      setstepUpResult(stepupResult);
      return
    }

    const years = targetAge - presentAge;
    const nper = years * 12;
    const monthlyRate = cagr / 100 / 12;

    // If monthlyRate is 0 (edge case), FV = P * nper
    let FV;
    if (monthlyRate === 0) {
      FV = monthlyInvestment * nper;
    } else {
      // FV for payments at end of period (Excel FV default, type = 0)
      // FV = P * ( (1+r)^n - 1 ) / r
      const factor = Math.pow(1 + monthlyRate, nper) - 1;
      FV = monthlyInvestment * (factor / monthlyRate);
    }

    const totalInvested = monthlyInvestment * nper;
    const FVInCrores = FV / 10000000;

    setResult({
      totalInvested,
      FV,
      FVInCrores,
      years,
      nper,
      monthlyRate,
    });
  };


  return (
    <>
    <div className={styles.container}>

      <div className={styles.card}>

        <h1 className={styles.title}>SIP Calculator</h1>

        <div className={styles.row}>

          <div className={styles.field}>
            <label>Annual Step Up (Optional)</label>
            <div
              className={`${styles.segmentToggle} ${
                form.stepUpType === "absolute" ? styles.segmentRight : ""
              }`}
            >
              <button
                type="button"
                className={form.stepUpType === "percentage" ? styles.segmentActive : styles.segment}
                onClick={() => setForm((prev) => ({ ...prev, stepUpType: "percentage" }))}
              >
                Percentage
              </button>
              <button
                type="button"
                className={form.stepUpType === "absolute" ? styles.segmentActive : styles.segment}
                onClick={() => setForm((prev) => ({ ...prev, stepUpType: "absolute" }))}
              >
                Amount
              </button>
            </div>
          </div>

          <div className={styles.field}>
            <label><br/></label>
            <input
              type="number"
              name="annualStepUp"
              value={form.annualStepUp}
              onChange={handleChange}
              placeholder={form.stepUpType === "percentage" ? "e.g. 10.00" : "e.g. 2,000"}
            />
          </div>
        </div>  

          <div className={styles.row}>
            <div className={styles.field}>
              <label>Present Age</label>
              <input
                type="number"
                name="presentAge"
                value={form.presentAge}
                onChange={handleChange}
                placeholder="e.g. 25"
              />
            </div>

            <div className={styles.field}>
              <label>Target Age</label>
              <input
                type="number"
                name="targetAge"
                value={form.targetAge}
                onChange={handleChange}
                placeholder="e.g. 60"
              />
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.field}>
              <label>Per Month Investment (Outflow)</label>
              <input
                type="number"
                name="monthlyInvestment"
                value={form.monthlyInvestment}
                onChange={handleChange}
                placeholder="e.g. 10000"
              />
            </div>

            <div className={styles.field}>
              <label>Expected CAGR (Annualized Return %)</label>
              <input
                type="number"
                name="cagr"
                value={form.cagr}
                onChange={handleChange}
                placeholder="e.g. 12"
              />
            </div>
          </div>

          <div className={styles.actions}>
            <button onClick={calculateSIP}>Calculate</button>
          </div>

          {error && <div className={styles.error}>{error}</div>}

          {(result || stepUpResult)  && (
          <>
          <div className={styles.cardHeader}  >
                <div
                  className={styles.pdfButton}
                  onClick={handleDownloadPdf}
                  title="Download PDF"
                >
                    Export <FaFilePdf size={20} color="#d32f2f" />
                </div>
          </div>
          <ResultCard result={result}  stepUpResult={stepUpResult} />
          </>  
          )}

      </div>
    </div>

    {(result || stepUpResult)  && (
    <PdfLayout ref={pdfRef} type="sip" form={form}  resultCard={{
        result,
        stepUpResult,
      }}   >
    </PdfLayout>
     )}

    </>


  );
};

export default SIPCalculator;
